import { LOGIN_URL, COGNITO_URL, API_URL } from './utils/constants';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const defaultUserData = {
  email: '',
  name: 'user',
  avatar: '/avatar48.png',
  env_name: '',
  org_name: '',
  org_id: ''
};

class Auth {
  constructor() {
    console.log('Auth constructor');
    // Updated to use cookies
    const userData = this.getCookie('userData');
    if (userData) {
      this.userData = JSON.parse(userData);
    } else {
      this.userData = defaultUserData;
    }
  }

  // Helper method to set a cookie
  setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
  }

  // Helper method to get a cookie value
  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Helper method to erase a cookie
  eraseCookie(name) {
    document.cookie = name + '=; Max-Age=-99999999;';
  }

  syncCurrentSession() {
    const idToken = this.getCookie('idToken');
    if (!idToken) {
      console.log('No access token available');
      return null;
    }
    const refreshToken = this.getCookie('refreshToken');
    if (!refreshToken) {
      console.log('No refresh token available');
    }
    // Assuming the accessToken has the expiration embedded within it as a JWT token
    const decodedToken = jwtDecode(idToken);
    const currentTime = Date.now() / 1000;
    if (decodedToken.exp < currentTime) {
      console.log('Access token expired');
      if (!refreshToken) {
        this.logout();
      }
      this.syncRefreshToken(refreshToken);
    }
    return {
      accessToken: this.getCookie('accessToken'),
      idToken: this.getCookie('idToken')
    };
  }

  async currentSession() {
    try {
      const idToken = this.getCookie('idToken');
      if (!idToken) {
        console.log('No access token available');
        return null;
      }

      const refreshToken = this.getCookie('refreshToken');
      if (!refreshToken) {
        console.log('No refresh token available');
      }

      // Assuming the accessToken has the expiration embedded within it as a JWT token
      const decodedToken = jwtDecode(idToken);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp < currentTime) {
        console.log('Access token expired');
        if (!refreshToken) {
          this.logout();
        }
        await this.refreshToken(refreshToken);
      }

      return {
        accessToken: this.getCookie('accessToken'),
        idToken: this.getCookie('idToken')
      };
    } catch (error) {
      console.error('Error during session validation:', error);
      return null;
    }
  }

  async refreshToken(refreshToken) {
    try {
      const response = await axios.get(`${API_URL}/auth/refresh?refresh_token=${refreshToken}`);
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      this.storeTokens(response.data);
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
  }

  syncRefreshToken() {
    const refreshToken = this.getCookie('refreshToken');
    if (!refreshToken) {
      console.log('No refresh token available');
      this.logout();
    }
    fetch(`${API_URL}/auth/refresh?refresh_token=${refreshToken}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          throw new Error(data.error);
        }
        console.log('Refreshed token');
        this.storeTokens(data);
        // reload the page
        window.location.reload();
      })
      .catch((error) => {
        console.error('Error refreshing token:', error);
      });
  }

  syncGetIdToken() {
    const data = this.syncCurrentSession();
    if (!data || !data.idToken) {
      console.error('No idToken available. User might not be logged in.');
      this.login_redirect();
      return null;
    }
    return data.idToken;
  }

  syncGetAccessToken() {
    const data = this.syncCurrentSession();
    if (!data || !data.idToken) {
      console.error('No access token available. User might not be logged in.');
      this.logout();
      this.login_redirect();
      return null;
    }
    return data.accessToken;
  }

  async getIdToken() {
    const data = await this.currentSession();
    if (!data || !data.idToken) {
      this.login_redirect();
      throw new Error('No idToken available. User might not be logged in.');
    }
    return data.idToken;
  }

  getCognitoUrl() {
    return `${COGNITO_URL}&redirect_uri=${this.getRedirectUri()}`;
  }

  getQueryParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  getRedirectUri() {
    const port = window.location.port ? `:${window.location.port}` : '';
    const baseUrl = `${window.location.protocol}//${window.location.hostname}${port}`;
    let redirect_uri = `${baseUrl}${LOGIN_URL}`;
    // Check for 'redirect_uri' parameter in the URL and use it if present
    const paramRedirectUri = this.getQueryParam('redirect_uri');
    if (paramRedirectUri) {
      redirect_uri = decodeURIComponent(paramRedirectUri);
    }
    return redirect_uri;
  }

  async exchangeCodeForToken(code) {
    if (!code) {
      console.error('No code in URL');
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/auth/token?code=${code}&redirect_url=${this.getRedirectUri()}`);
      if (response.data.error) {
        var error = response.data.error;
        console.error('Error exchanging code for token:', error);
        throw new Error(error);
      }
      this.storeTokens(response.data);
      window.location.reload();
    } catch (error) {
      if (!this.isActiveSession()) {
        throw new Error('Error exchanging code for token! Please try again or contact support.');
      }
      console.error('Error exchanging code for token:', error);
    }
  }

  isActiveSession() {
    const accessToken = this.getCookie('accessToken');
    if (!accessToken) {
      console.log('No access token available');
      return false;
    }
    const decodedToken = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp > currentTime;
  }

  cacheUserData(decodedToken) {
    this.userData = {
      email: decodedToken.email,
      name: decodedToken.name,
      avatar: decodedToken.picture,
      env_name: decodedToken['custom:env_name'],
      org_name: decodedToken['custom:org_name'],
      org_id: decodedToken['custom:org_id']
    };
    console.log('User data:', this.userData);
    this.setCookie('userData', JSON.stringify(this.userData));
  }

  storeTokens(data) {
    console.log('Storing tokens:', data);
    this.setCookie('accessToken', data.access_token);
    this.setCookie('idToken', data.id_token);
    if (data.refresh_token) {
      this.setCookie('refreshToken', data.refresh_token);
    }
    const decodedToken = jwtDecode(data.id_token);
    this.cacheUserData(decodedToken);
  }

  login_redirect() {
    window.location.href = LOGIN_URL;
  }

  logout() {
    console.log('Logging out');
    this.eraseCookie('accessToken');
    this.eraseCookie('refreshToken');
    this.eraseCookie('idToken');
    this.eraseCookie('referral_id');
    this.eraseCookie('userData');

    // if the page is already at the login URL
    if (window.location.pathname === LOGIN_URL) {
      return;
    }
    this.userData = defaultUserData;
    //this.login_redirect();
  }
}

const auth = new Auth();
export default auth;
